"use client";

import * as React from "react";
import { Expand } from "lucide-react";

import { useTransientScrollState } from "@/components/site/layout/use-transient-scroll-state";
import { OverviewPreview } from "@/components/site/sections/six-pillars-preview/overview-pane";
import {
  CandidatesPreview,
  CoopProfilePreview,
  FloatingBoundaryContext,
  FundamentalsPreview,
  PulsePreview,
} from "@/components/site/sections/six-pillars-preview/shared-surface";
import { RandomCoffeePreview } from "@/components/site/sections/six-pillars-preview/random-coffee-preview";
import { type SixPillarPreviewId } from "@/lib/six-pillars-preview";
import { cn } from "@/lib/utils";

type PreviewSurfaceProps = {
  pillarId: SixPillarPreviewId;
  variant: "compact" | "expanded";
  embedded?: boolean;
  onOpenFullscreen?: () => void;
};

export const previewTitles: Record<SixPillarPreviewId, string> = {
  overview: "Overview",
  "coop-profile": "Coop Profile",
  fundamentals: "Fundamentals",
  pulse: "Pulse",
  candidates: "Candidates",
  "random-coffee": "Random Coffee",
};

/** Pillars that open on the product's dark stage. */
const STAGE_PILLARS = new Set<SixPillarPreviewId>(["fundamentals", "pulse", "random-coffee"]);

export function SixPillarsPreviewSurface({
  pillarId,
  variant,
  embedded = false,
  onOpenFullscreen,
}: PreviewSurfaceProps) {
  const compact = variant === "compact";
  const boundaryRef = React.useRef<HTMLDivElement>(null);
  const { isScrolling, handleScroll } = useTransientScrollState();

  return (
    <FloatingBoundaryContext.Provider value={{ boundaryRef }}>
      <div
        ref={boundaryRef}
        className={cn(
          "group/preview relative overflow-hidden bg-muted/18 [font-family:inherit] transition-[border-color,box-shadow,transform] duration-300",
          compact && !embedded
            ? "rounded-[1.55rem] border border-border/60 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.16)] hover:border-border/80 hover:shadow-[0_22px_54px_-34px_rgba(15,23,42,0.2)]"
            : "rounded-none border-0 shadow-none",
          !compact && "h-full",
        )}
      >
        <div
          data-scroll-active={isScrolling ? "true" : "false"}
          onScroll={handleScroll}
          className={cn(
            "site-scrollbar-auto overflow-auto overscroll-contain [scrollbar-gutter:stable]",
            compact
              ? "h-[calc(24rem-55px)] md:h-[calc(25rem-55px)]"
              : "h-full max-h-[calc(100vh-7rem)]",
          )}
        >
          <PreviewRenderer pillarId={pillarId} variant={variant} />
        </div>

        {compact ? (
          <>
            {/* A white fade over a dark stage reads as fog; the stage's own edge marks the cut there. */}
            <div
              aria-hidden
              className={cn(
                STAGE_PILLARS.has(pillarId) && "hidden",
                "pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(250,250,250,0.96)_100%)]",
                !embedded && "rounded-b-[1.55rem]",
              )}
            />
            <button
              type="button"
              aria-label={`Open ${previewTitles[pillarId]} in full screen`}
              onClick={onOpenFullscreen}
              className="site-pressable absolute right-4 bottom-4 z-[50] inline-flex size-11 items-center justify-center rounded-full border border-border/75 bg-background/98 text-foreground backdrop-blur-sm [box-shadow:0_14px_30px_-18px_rgba(15,23,42,0.24),0_0_0_7px_rgba(255,255,255,0.92)] transition-transform duration-300 group-hover/preview:-translate-y-0.5 animate-[roadmap-fullscreen-pulse_1.55s_ease-in-out_infinite]"
            >
              <Expand className="size-4" />
            </button>
          </>
        ) : null}
      </div>
    </FloatingBoundaryContext.Provider>
  );
}

function PreviewRenderer({
  pillarId,
  variant,
}: {
  pillarId: SixPillarPreviewId;
  variant: "compact" | "expanded";
}) {
  switch (pillarId) {
    case "overview":
      return <OverviewPreview variant={variant} />;
    case "coop-profile":
      return <CoopProfilePreview variant={variant} />;
    case "fundamentals":
      return <FundamentalsPreview variant={variant} />;
    case "pulse":
      return <PulsePreview variant={variant} />;
    case "candidates":
      return <CandidatesPreview variant={variant} />;
    case "random-coffee":
      return <RandomCoffeePreview variant={variant} />;
    default:
      return null;
  }
}
